import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { ArrowLeft, LogIn, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const from = location.state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      toast.success("Signed in");
      navigate(from, { replace: true });
    } else {
      setError(r.error);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 sm:px-6 md:px-10 py-8 sm:py-14">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      <div className="mb-10">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
          Sign in
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
          Welcome back.
        </h1>
      </div>

      <form
        onSubmit={submit}
        className="space-y-6 rounded-md border border-border bg-white p-6 md:p-8"
      >
        <div className="space-y-2">
          <Label className="text-xs tracking-[0.15em] uppercase font-semibold">Email</Label>
          <Input
            data-testid="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="h-11 rounded-md"
            required
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-[0.15em] uppercase font-semibold">Password</Label>
          <Input
            data-testid="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-11 rounded-md"
            required
          />
        </div>
        {error && (
          <div
            data-testid="login-error"
            className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-xs text-destructive"
          >
            {error}
          </div>
        )}
        <Button
          type="submit"
          data-testid="login-submit"
          disabled={loading}
          className="w-full rounded-md h-11 gap-2"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-4 w-4" /> Sign in</>}
        </Button>

        <div className="flex justify-between text-xs">
          <Link
            to="/forgot-password"
            data-testid="forgot-link"
            className="text-muted-foreground hover:text-foreground"
          >
            Forgot password?
          </Link>
          <Link to="/register" data-testid="register-link" className="font-semibold hover:text-primary">
            Create an account
          </Link>
        </div>
      </form>
    </div>
  );
}
